import { UploadInProgressFile } from "../models/upload-in-progress-file";
import { FileUtils } from "./file-utils";
import { toCompactReadableBytes } from "./file/file-size-formatter";
import {
    GIF_SOURCE_VIDEO_EXTENSIONS,
    gifEstimateSourceKind,
    gifEstimateStore,
    gifEstimatedNaturalBytes,
} from "./gif-estimate.store";

/**
 * GIF "Target file size" per-file guidance tiers (docs/GIF_EXACT_SIZE_DESIGN.md §6).
 * ratio = targetBytes / originalBytes. Single source of truth for calibration updates.
 *
 * Calibrated on the 2026-08 solver run over the real-GIF corpus: the solver reaches
 * the byte target at ~100% down to ratio 0.3 and 97.7% at 0.1, so the tiers convey the
 * QUALITY the target allows, not hit probability — accepted-candidate SSIM medians:
 * ratio 0.5 -> 0.97, 0.3 -> 0.94, 0.1 -> 0.86.
 */
export const GIF_TARGET_TIERS = {
    /** ratio >= this -> "Likely ✓" (quality essentially preserved) */
    LIKELY_MIN_RATIO: 0.45,
    /** ratio >= this (and < LIKELY_MIN_RATIO) -> "Best-effort ⚠" (noticeable trade) */
    BEST_EFFORT_MIN_RATIO: 0.15,
    /** ratio below this triggers the red "changes how the GIF looks" warning */
    RED_ALERT_MAX_RATIO: 0.7,
};

/**
 * Parse the xconfig `value::unit` encoded target-size string (e.g. "8::MB",
 * as produced by TARGET_FILE_SIZE_SUBGROUP's BYTE_SIZE_TEXT_INPUT_DROPDOWN /
 * TextInputDropdownViewState.combinedValue) into a byte count.
 *
 * Mirrors the decoding already done in file-info-box.component.tsx
 * (updateEstimatedOutputFileSize) and the SI (1000-based) multipliers in
 * springboot-shared-library's ByteUnits.convertUnitValueToBytes (kB=1e3,
 * MB=1e6) that the backend uses to interpret the same string.
 */
export const parseGifTargetSizeBytes = (fileSize: string | null | undefined): number | null => {
    if (!fileSize || typeof fileSize !== 'string') {
        return null;
    }
    const parts = fileSize.split('::');
    if (parts.length !== 2) {
        return null;
    }
    const value = parseFloat(parts[0]);
    if (!isFinite(value) || value <= 0) {
        return null;
    }
    const unit = parts[1];
    if (unit === 'MEGABYTES' || unit === 'MB') {
        return value * 1000 * 1000;
    }
    if (unit === 'KILOBYTES' || unit === 'kB' || unit === 'KB') {
        return value * 1000;
    }
    // BYTES / B / anything else: already in bytes.
    return value;
};

/**
 * Tier label for a target/original ratio, per GIF_TARGET_TIERS.
 */
export const gifTargetSizeTierLabel = (ratio: number): string => {
    if (ratio >= GIF_TARGET_TIERS.LIKELY_MIN_RATIO) {
        return 'Likely ✓';
    }
    if (ratio >= GIF_TARGET_TIERS.BEST_EFFORT_MIN_RATIO) {
        return 'Best-effort ⚠';
    }
    return 'Aggressive 🔴';
};

export interface GifTargetSizeDataFill {
    results: {
        compression?: string;
        fileSize?: string;
        toExtension?: string;
        /** v5 Max-file-size vocabulary (GIF_EXACT_SIZE_DESIGN.md §6) */
        sizeLimit?: string;
        sizePercentage?: string | number;
    };
    uploadInProgressFiles?: UploadInProgressFile<any>[];
    /** The page's option tree (every Note.fn call site passes it): named size-limit presets carry their cap there. */
    context?: GifPresetTree;
}

/** What `gifPresetBytes` needs from ConvertOptionsContextV2. */
export interface GifPresetTree {
    getViewStates(propertyName: string): ReadonlyArray<unknown> | undefined;
}

/**
 * v5 size-limit preset bytes — DISPLAY MATH ONLY. The wire carries the preset id (sizeLimit=PRESET_*); the backend
 * maps bytes. The cap is `data.bytes` on the preset's xconfig node (xconfig src/data/misc/gif-size-limit-presets.json,
 * SI decimal, 1 MB = 1e6 B), read from the page's option tree: the `sizeLimit` dropdown's entries. ironman
 * GifCompressionV2.SIZE_LIMIT_PRESET_BYTES is pinned to the same file. Null when the tree has no such entry.
 */
export const gifPresetBytes = (tree: GifPresetTree | undefined, presetId: string): number | null => {
    for (const holder of tree?.getViewStates?.('sizeLimit') ?? []) {
        for (const leaf of ((holder as { options?: unknown[] }).options ?? [])) {
            for (const entry of ((leaf as { options?: unknown[] })?.options ?? [])) {
                const e = entry as { value?: string; data?: { bytes?: unknown } };
                if (e?.value === presetId && typeof e.data?.bytes === 'number' && e.data.bytes > 0) return e.data.bytes;
            }
        }
    }
    return null;
};

/**
 * v5 severity tiers (GIF_EXACT_SIZE_DESIGN.md §6, corpus-measured boundaries):
 * fraction = derived per-file byte target / estimated natural GIF bytes.
 * Config data, not code — ordered highest-first; first tier whose minFraction
 * the value meets wins. `label` is the human severity wording the note lines
 * render next to the marker.
 */
export const GIF_V5_SEVERITY_TIERS = [
    { severity: 'green', minFraction: 0.70, marker: '🟢', label: 'light compression' },
    { severity: 'yellow', minFraction: 0.45, marker: '🟡', label: 'moderate compression' },
    { severity: 'red', minFraction: 0, marker: '🔴', label: 'strong compression' },
] as const;

/**
 * v5 verdict-note copy — every user-facing string of the Max-file-size note in
 * one config structure (severity labels live on GIF_V5_SEVERITY_TIERS).
 * The inflation explainer renders ONCE per note: inline in the single-file
 * line, hoisted above the list for multi-file. The refining/estimating spans
 * carry the global pulse classes defined in styles/globals.scss
 * (.gif-estimate-refining / .gif-estimate-provisional / .gif-estimate-pulse).
 */
export const GIF_V5_VERDICT_COPY = {
    // NOT "Max file size — per file": that phrase already labels the mode
    // button AND (in value mode) a control row, so three different things in
    // one card read as the same thing. The note is the only "per file" thing.
    header: '<div><strong>Per file:</strong></div>',
    /** Inline (single-file) parenthetical after "GIF ≈ X". */
    inflationInline: ' (GIFs store every frame — much larger than video)',
    /** Hoisted (multi-file) one-liner above the list. */
    inflationHoisted: '<div>GIFs store every frame, so a GIF is much larger than its source video.</div>',
    /** Animated while the sample-refined Stage-2 estimate is still on its way (~10 s of sample encodes). */
    refiningHtml: ' <span class="gif-estimate-refining">refining estimate… (~10 s)</span>',
    /** Animated pre-estimate placeholder for video sources. */
    estimatingHtml: '<span class="gif-estimate-refining">estimating GIF size…</span>',
    alreadyUnder: ' — already under your max ✓',
    /** Hint appended to non-green lines; bytes = green minFraction x estimate. */
    goodQualityHint: (bytesReadable: string) => ` (for good quality allow ≥ ${bytesReadable})`,
};

export type GifV5SeverityTier = (typeof GIF_V5_SEVERITY_TIERS)[number];

export const gifV5SeverityFor = (fraction: number): GifV5SeverityTier =>
    GIF_V5_SEVERITY_TIERS.find(tier => fraction >= tier.minFraction)
    ?? GIF_V5_SEVERITY_TIERS[GIF_V5_SEVERITY_TIERS.length - 1];

/**
 * THE per-file target derivation for the v5 Max-file-size mode — one function
 * for every entry type. Percent resolves against THAT file's source bytes;
 * absolute entries (Custom size value / presets) are per-file caps.
 * Returns null when the target can't be derived (unknown entry, unparsable
 * value, percent without a file size).
 */
export const deriveGifTargetBytes = (
    results: GifTargetSizeDataFill['results'],
    fileSizeBytes: number | undefined | null,
    tree?: GifPresetTree,
): number | null => {
    switch (results.sizeLimit) {
        case 'CUSTOM_SIZE_PERCENT': {
            const percent = Number(results.sizePercentage);
            if (!isFinite(percent) || percent <= 0) return null;
            if (typeof fileSizeBytes !== 'number' || !isFinite(fileSizeBytes) || fileSizeBytes <= 0) return null;
            return Math.round((percent / 100) * fileSizeBytes);
        }
        case 'CUSTOM_SIZE_VALUE':
            return parseGifTargetSizeBytes(results.fileSize);
        default:
            return results.sizeLimit ? gifPresetBytes(tree, results.sizeLimit) : null;
    }
};

/**
 * @see GIF_TARGET_SIZE_PER_FILE_FN registration in convert-options.view-states.tsx (Note.fn)
 *
 * Per-file "Target file size" guidance for the GIF compress/convert pages:
 * for each uploaded file, shows original size -> target size, % of original,
 * and a reachability tier. Returns '' when target mode isn't selected, there
 * are no files yet, or the target size can't be parsed (matches the
 * empty-string-means-render-nothing convention used by the other Note.fn
 * helpers in this file).
 */

/**
 * Merge the two upload lists the converter page keeps: the FAKE placeholder
 * (instant skeleton feedback, alive from file-select until UPLOAD COMPLETES)
 * and the real metadata entry (exists from upload START). Mid-upload both
 * contain the same file, so consumers rendering the concat (the per-file
 * target-size notes) showed every file twice. A fake is redundant the moment
 * its real entry exists — matched by underlying File identity, with a
 * name+size fallback for safety.
 */
export const mergeUploadInProgressLists = (
    fakes: UploadInProgressFile<any>[] | undefined,
    real: UploadInProgressFile<any>[] | undefined,
): UploadInProgressFile<any>[] => {
    const realList = real ?? [];
    const fakeList = fakes ?? [];
    if (fakeList.length === 0) return realList;
    const realFiles = new Set(realList.map(r => r.file));
    const realKeys = new Set(realList.map(r => {
        const f = r.getFile ? r.getFile() : r.file;
        return f ? `${f.name}::${f.size}` : '';
    }));
    const pendingFakes = fakeList.filter(fk => {
        if (realFiles.has(fk.file)) return false;
        const f = fk.getFile ? fk.getFile() : fk.file;
        return !(f && realKeys.has(`${f.name}::${f.size}`));
    });
    return [...pendingFakes, ...realList];
};

/**
 * v5 "Max file size" per-file verdict lines (design §6 severity signalling):
 * for each file, derived target vs estimated natural GIF size with a
 * config-driven severity marker; "already under" when the cap clears the
 * estimate; "estimating…" until a stage lands on video sources; an
 * achievability warning when the cap sits below the file's measured floor;
 * and an aggregate count line for multi-file uploads.
 *
 * Registered as its own Note.fn AND reachable through
 * GIF_TARGET_SIZE_PER_FILE_FN (which delegates when compression=MAX_FILE_SIZE)
 * so the existing note node on TARGET_FILE_SIZE_SUBGROUP renders verdicts in
 * Custom-size-value mode without an xconfig change.
 */
export const GIF_MAX_FILE_SIZE_VERDICT_FN = (dataFill: GifTargetSizeDataFill): string => {
    try {
        const results = dataFill?.results ?? {};
        if (results.compression !== 'MAX_FILE_SIZE') {
            return '';
        }
        const mode = results.sizeLimit;
        const isPercentMode = mode === 'CUSTOM_SIZE_PERCENT';
        // Absolute modes derive one target for every file; percent derives per file.
        let absoluteTargetBytes: number | null = null;
        if (isPercentMode) {
            const percent = Number(results.sizePercentage);
            if (!isFinite(percent) || percent <= 0) return '';
        } else {
            absoluteTargetBytes = deriveGifTargetBytes(results, 1, dataFill.context);
            if (absoluteTargetBytes === null) return '';
        }
        const targetDescription = isPercentMode
            ? `${Number(results.sizePercentage)}% of source size`
            : toCompactReadableBytes(absoluteTargetBytes);

        const standingNotes =
            '<div>Best effort — we aim just under your max. For very complex files the smallest '
            + 'good-quality output may come out larger; we\'ll tell you if that happens.</div>'
            + '<div>Max-size mode runs several encodes — large files can take a few minutes.</div>';

        const files = dataFill.uploadInProgressFiles;
        if (!files || files.length === 0) {
            // House pattern: never an empty box — describe the cap until files arrive.
            // A cap ABOVE 100% of the source only makes sense once you know a GIF
            // is far larger than the video it came from; without that, the video
            // pages' 350% default reads as "this will make my file 3.5x bigger".
            const capExceedsSource = isPercentMode && Number(results.sizePercentage) > 100;
            return GIF_V5_VERDICT_COPY.header
                + `<div>Each file will be capped at ${targetDescription}.`
                + (capExceedsSource
                    ? ' A GIF stores every frame, so it lands well above the size of the video it came from —'
                    + ' the cap is a share of that larger GIF.'
                    : '')
                + ' Add files to see per-file estimates and quality verdicts.</div>'
                + standingNotes;
        }

        const greenTier = GIF_V5_SEVERITY_TIERS[0];
        const multiFile = files.length > 1;
        let degradedCount = 0;
        let reducedCount = 0;
        let allVideos = true;
        // The inflation explainer renders ONCE per note: inline in the line
        // when there is a single file, hoisted above the list for multi-file.
        let anyVideoEstimateShown = false;
        const warningLines: string[] = [];

        const items = files.map(f => {
            const file = f.getFile();
            const name = (file && file.name) || 'file';
            const sourceBytes = file && file.size > 0 ? file.size : undefined;
            const kind = gifEstimateSourceKind(name);
            if (kind !== 'video') allVideos = false;

            const targetBytes = deriveGifTargetBytes(results, sourceBytes, dataFill.context);
            const targetReadable = targetBytes !== null
                ? toCompactReadableBytes(targetBytes)
                : targetDescription;

            // Estimated natural GIF size: gif sources ARE their natural size
            // (synchronous, nothing to estimate); video sources read the staged
            // estimate; other sources have no estimator — show the cap only.
            // gifEstimatedNaturalBytes prefers the Stage-2 measured approxSize
            // over the pixel-frames model whenever the probe delivered one.
            const record = gifEstimateStore.get((f as any).id);
            const estimate = kind === 'gif' ? sourceBytes : (kind === 'video' ? gifEstimatedNaturalBytes(record) : undefined);

            if (targetBytes === null || estimate === undefined || estimate <= 0) {
                if (kind === 'video') {
                    const src = sourceBytes ? `video ${toCompactReadableBytes(sourceBytes)} → ` : '';
                    return `<li><b>${name}</b>: ${src}${GIF_V5_VERDICT_COPY.estimatingHtml} · max ${targetReadable}</li>`;
                }
                return `<li><b>${name}</b>: max ${targetReadable}</li>`;
            }

            const estimateReadable = toCompactReadableBytes(estimate);
            const refining = kind === 'video' && record && record.stage < 2
                ? GIF_V5_VERDICT_COPY.refiningHtml : '';

            // Line base names what the numbers ARE: video sources show
            // "video <source bytes> → GIF ≈ <estimate>"; gif→gif shows the
            // file's own size as "original <bytes>" — no estimate language.
            let base: string;
            if (kind === 'video') {
                anyVideoEstimateShown = true;
                const src = sourceBytes ? `video ${toCompactReadableBytes(sourceBytes)} → ` : '';
                base = `${src}GIF ≈ ${estimateReadable}${multiFile ? '' : GIF_V5_VERDICT_COPY.inflationInline}`;
            } else {
                base = `original ${estimateReadable}`;
            }

            if (record?.floorSize && targetBytes < record.floorSize) {
                const floorReadable = toCompactReadableBytes(record.floorSize);
                const prefix = multiFile ? `<b>${name}</b>: ` : '';
                warningLines.push(`<div class="mt-2 fw-semibold text-danger">${prefix}${targetReadable} is below what `
                    + `this file can reach with acceptable quality (~${floorReadable}) — we'll get as close as possible.</div>`);
            }

            if (targetBytes >= estimate) {
                return `<li><b>${name}</b>: ${base}${GIF_V5_VERDICT_COPY.alreadyUnder}${refining}</li>`;
            }

            const fraction = targetBytes / estimate;
            const tier = gifV5SeverityFor(fraction);
            if (tier.severity === 'red') {
                degradedCount++;
            } else if (tier.severity === 'yellow') {
                reducedCount++;
            }
            // While the estimate is still model-based on a video source the
            // marker reads as provisional: pulsing at reduced opacity before
            // Stage 2, steady reduced opacity when Stage 2 came back quickOnly
            // (no refinement is coming — the model estimate is final).
            let marker: string = tier.marker;
            if (kind === 'video' && record) {
                if (record.stage < 2) {
                    marker = `<span class="gif-estimate-provisional gif-estimate-pulse">${tier.marker}</span>`;
                } else if (record.quickOnly) {
                    marker = `<span class="gif-estimate-provisional">${tier.marker}</span>`;
                }
            }
            const percent = Math.round(fraction * 100);
            if (kind === 'video') {
                const hint = tier.severity !== 'green'
                    ? GIF_V5_VERDICT_COPY.goodQualityHint(toCompactReadableBytes(greenTier.minFraction * estimate))
                    : '';
                return `<li><b>${name}</b>: ${base} · your max ${targetReadable} keeps ${percent}% `
                    + `→ ${tier.label} ${marker}${hint}${refining}</li>`;
            }
            return `<li><b>${name}</b>: ${base} → max ${targetReadable} (${percent}%) → ${tier.label} ${marker}${refining}</li>`;
        }).join('');

        let html = GIF_V5_VERDICT_COPY.header;
        if (multiFile && anyVideoEstimateShown) {
            html += GIF_V5_VERDICT_COPY.inflationHoisted;
        }
        html += `<ul>${items}</ul>`;
        if (multiFile && degradedCount > 0) {
            const noun = allVideos ? 'videos' : 'files';
            html += `<div class="mt-2 fw-semibold text-danger">${degradedCount} of ${files.length} ${noun} `
                + `will lose significant quality at ${targetDescription}.</div>`;
        }
        if (multiFile && reducedCount > 0) {
            const noun = allVideos ? 'videos' : 'files';
            html += `<div class="mt-2 fw-semibold">${reducedCount} of ${files.length} ${noun} `
                + `may lose some quality at ${targetDescription}.</div>`;
        }
        html += warningLines.join('');
        html += standingNotes;
        return html;
    } catch (e) {
        console.error(e);
    }
    return '';
};

export const GIF_TARGET_SIZE_PER_FILE_FN = (dataFill: GifTargetSizeDataFill): string => {
    try {
        const results = dataFill?.results ?? {};
        if (results.compression === 'MAX_FILE_SIZE') {
            // v5 tree: the same note node under Custom size value renders the
            // v5 verdicts; the v4 branch below keeps serving the old tree.
            return GIF_MAX_FILE_SIZE_VERDICT_FN(dataFill);
        }
        if (results.compression !== 'FILE_SIZE_EXACT_WITH_AUTO_SCALE') {
            return '';
        }
        const files = dataFill.uploadInProgressFiles;
        if (!files || files.length === 0) {
            // House pattern (APPROX_FILE_SIZE_REDUCTION_FN): never an empty box —
            // show a generic worked example until files arrive. The time-note line is
            // byte-identical to the post-upload note so the box doesn't reflow its tail.
            return '<div><strong>Target size per file:</strong></div>'
                + '<div>Add a GIF to see its current size and how far your target is from it. '
                + 'As a guide: a 5 MB GIF targeted at 3.5 MB (70%) usually looks unchanged; '
                + 'targeted at 1.5 MB (30%) expect fewer colors, smaller dimensions or fewer frames.</div>'
                + '<div>Target mode runs several encodes — large GIFs can take a few minutes.</div>';
        }
        const targetBytes = parseGifTargetSizeBytes(results.fileSize);
        if (targetBytes === null) {
            return '';
        }

        let anyBelowRedAlertThreshold = false;

        const items = files.map(f => {
            const file = f.getFile();
            const name = (file && file.name) || 'file';
            let originalSize = file ? file.size : 0;
            const targetReadable = FileUtils.toReadableBytes(targetBytes, 'quantity');

            if (!originalSize || originalSize <= 0) {
                return `<li><b>${name}</b>: target ${targetReadable}</li>`;
            }

            // Video sources: the target applies to the GIF OUTPUT, which inflates
            // ~4.9x over the video bytes (prod median, 30d mp4->gif data) — comparing
            // the target against the VIDEO size falsely reads "already under target".
            const ext = (name.split('.').pop() || '').toLowerCase();
            const isVideo = GIF_SOURCE_VIDEO_EXTENSIONS.includes(ext);
            if (isVideo) {
                const estGif = Math.round(originalSize * 4.9);
                const estReadable = FileUtils.toReadableBytes(estGif, 'quantity');
                if (targetBytes >= estGif) {
                    return `<li><b>${name}</b>: estimated GIF ≈ ${estReadable} (videos grow when converted to GIF) — likely already under target</li>`;
                }
                const vRatio = targetBytes / estGif;
                const vTier = gifTargetSizeTierLabel(vRatio);
                if (vRatio < GIF_TARGET_TIERS.RED_ALERT_MAX_RATIO) {
                    anyBelowRedAlertThreshold = true;
                }
                return `<li><b>${name}</b>: estimated GIF ≈ ${estReadable} → ${targetReadable} (${Math.round(vRatio * 100)}% of estimate) — ${vTier}</li>`;
            }

            const originalReadable = FileUtils.toReadableBytes(originalSize, 'quantity');

            if (targetBytes >= originalSize) {
                return `<li><b>${name}</b>: ${originalReadable} — already under target — output will simply be optimized</li>`;
            }

            const ratio = targetBytes / originalSize;
            const percent = Math.round(ratio * 100);
            const tier = gifTargetSizeTierLabel(ratio);

            if (ratio < GIF_TARGET_TIERS.RED_ALERT_MAX_RATIO) {
                anyBelowRedAlertThreshold = true;
            }

            return `<li><b>${name}</b>: ${originalReadable} → ${targetReadable} (${percent}% of original) — ${tier}</li>`;
        }).join('');

        let html = '<div><strong>Target size per file:</strong></div>'
            + `<ul>${items}</ul>`
            + '<div>Target mode runs several encodes — large GIFs can take a few minutes.</div>';

        if (anyBelowRedAlertThreshold) {
            // Plain emphasized text, NOT a nested alert — this whole note already renders
            // inside an alert-warning shell; stacking a second alert chrome inside it
            // reads as two broken boxes.
            html += '<div class="mt-2 fw-semibold text-danger">Targets below 70% of the original usually change how the GIF looks '
                + '(fewer colors, smaller dimensions, or fewer frames). We\'ll disclose exactly what changed on the result.</div>';
        }

        return html;
    } catch (e) {
        console.error(e);
    }
    return '';
};

/**
 * @see UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN on global-functions.ts for method description
 */
export const UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN = (selectedPercentage: number, uploadInProgressFiles: UploadInProgressFile<any>[]): string => {
    if (selectedPercentage == 0 || !uploadInProgressFiles || uploadInProgressFiles.length == 0) {
        return ''
    }
    try {
        const value = Number(selectedPercentage);
        const valueReduction = 100 - value;
        const percentageReduction = 100 - Math.pow(value / 100, 2) * 100;

        return `<ul>` + uploadInProgressFiles.map(f => {
            const file = f.getFile();

            const originalSize = file.size;
            const idealReducedSize = originalSize * (value / 100) ** 2; // Calculate ideal reduced size
            const newSizeBest = FileUtils.toReadableBytes(idealReducedSize, 'quantity');
            const filename = file.name;
            const filesizeSource = FileUtils.toReadableBytes(originalSize, 'quantity');
            return `<li><b>${filename}</b> will reduce from ${filesizeSource} to ${newSizeBest}</li>`;
        }).join("")
        + '</ul>';
    } catch (e) {
        console.error(e);
    }
    return '';
};

/**
 * @see UPLOADED_FILE_TARGET_SIZE_FN on global-functions.ts for method description
 */
export const UPLOADED_FILE_TARGET_SIZE_FN = (selectedPercentage: number, uploadInProgressFiles: UploadInProgressFile<any>[]): string => {
    if (selectedPercentage == 0 || !uploadInProgressFiles || uploadInProgressFiles.length == 0) {
        return ''
    }
    try {
        return `Target Size: ${selectedPercentage}% of Original <br/>` + uploadInProgressFiles.map(f => {
            const file = f.getFile();
            const filename = file.name;
            const filesizeSource = FileUtils.toReadableBytes(file.size, 'quantity');
            const filesizeTarget = FileUtils.toReadableBytes(file.size * selectedPercentage / 100, 'quantity');
            return `- <b>${filename}:</b> Will reduce from ${filesizeSource} to ${filesizeTarget} (${selectedPercentage}% of original)`;
        }).join("<br/>");
    } catch (e) {
        console.error(e);
    }
    return '';
};

export const TIME_VALIDATE_FN = (value: string, type: string): string => {
    if (type === 'seconds') {
        if (!value.match(/^\d+(\.\d{1,3}){0,1}$/)) {
            return `Accepted value examples i.e 23 (for 23 seconds) \
            or 23.453 for (23 seconds and 453 milliseconds)`;
        }
    }
    if (type === 'formated_time') {
        if (!value.match(/^(\d{1,2}:){0,2}(\d{1,2})(\.\d{1,3}){0,1}$/)) {
            return `Accepted value examples i.e 23 (for 23 seconds) \
            or 23.453 for (23 seconds and 453 milliseconds)
            or 12:23.453 for (12 minutes 23 seconds and 453 milliseconds)
            or 3:1:23.453 for (3 hours 1 minute 23 seconds and 453 milliseconds)`;
        }
    }
    return null;
};

export const OUTPUT_EXT_FROM_OPTIONS_FN = (allOptions: any) => {
    if (allOptions.formCategories) {
        const outputExtensionOptions = allOptions.formCategories
            .find(f => f.category === 'outputExt');
        const outputExtensionOption = outputExtensionOptions && outputExtensionOptions.formGroups[0];
        return outputExtensionOption ? outputExtensionOption.selections.find(a => a.selected).formValue : null;
    }
    return null;
};


export const OUTPUT_VIDEO_ENCODER_FROM_OPTIONS_FN = (allOptions: any, categoryName: string, formGroupName: string) => {
    const category = allOptions.formCategories.find(cat => cat.category === categoryName);

    if (category) {
        const formGroup = category.formGroups.find(group => group.formGroupName === formGroupName);

        if (formGroup) {
            const selectedItem = formGroup.selections.find(selection => selection.selected);

            if (selectedItem) {
                return selectedItem.formValue;
            }

            // Check nested options recursively:
            for (const selection of formGroup.selections) {
                if (selection.formChildren) {
                    for (const child of selection.formChildren) {
                        if (child.formGroup && child.formGroup.selections) {
                            const nestedSelectedItem = child.formGroup.selections.find(nestedSelection => nestedSelection.selected);
                            if (nestedSelectedItem) {
                                return nestedSelectedItem.formValue;
                            }
                        }
                    }
                }
            }
        }
    }

    return null; // Return null if no item is found
}